import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Mail, ExternalLink, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigestPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/digest/subscription`, { headers: auth });
        if (r.ok) setSubscribed((await r.json()).subscribed);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const toggle = async (v) => {
    setSubscribed(v);
    const r = await fetch(`${API}/digest/subscribe`, {
      method: 'POST', headers: auth, body: JSON.stringify({ subscribed: v }),
    });
    if (r.ok) toast.success(v ? 'Subscribed to daily digest' : 'Unsubscribed');
    else toast.error('Failed to save');
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/digest/send-test`, { method: 'POST', headers: auth });
      const d = await r.json();
      if (d.sent) toast.success('Test digest sent — check your inbox');
      else toast.error(d.reason || `Send failed (${d.status})`);
    } finally { setSending(false); }
  };

  const previewUrl = `${API}/digest/preview`;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="digest-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Mail className="w-6 h-6 text-indigo-500" /> Daily Digest
        </h1>
        <p className="text-slate-500 mt-1">One-page email at 6 AM: yesterday's labels, protection revenue, RiskChoice overrides, open claims.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Subscription</CardTitle><CardDescription>Delivered to your account email.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Daily digest email</div>
                <div className="text-xs text-slate-500 mt-1">Fires from an external cron trigger — see /api/digest/run-now.</div>
              </div>
              <Switch checked={subscribed} onCheckedChange={toggle} data-testid="digest-toggle" />
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" asChild data-testid="preview-btn">
              <a href={previewUrl} target="_blank" rel="noreferrer">Preview yesterday's digest <ExternalLink className="w-3 h-3 ml-1" /></a>
            </Button>
            <Button size="sm" onClick={sendTest} disabled={sending} data-testid="send-test-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send me a test
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-50 dark:bg-slate-900/40">
        <CardHeader><CardTitle className="text-base">What you'll get</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1.5 text-slate-600 dark:text-slate-400">
          <div>· Yesterday's label count</div>
          <div>· Protection revenue (Selle10 + Parcel Risk + RiskChoice program fees)</div>
          <div>· RiskChoice overrides (when you picked something other than the auto pick)</div>
          <div>· Alerts for any open claim &gt; 24h</div>
          <div>· Top 10 recent tracking numbers with tier + SLT verification numbers</div>
        </CardContent>
      </Card>
    </div>
  );
}
